import RGL from 'react-grid-layout';
import { useForm } from 'react-hook-form';

import { Dashboard } from 'types/dashboard';
import { MarkdownWidget } from 'types/dashboard_widget';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';
import { useModal } from 'hooks/useModal';
import { useDashboard } from 'dashboards/hooks/useDashboard';

import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { Textarea } from 'components/Textarea/Textarea';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

type MarkdownWidgetFieldValues = { name: string; content: string };

type MarkdownFormProps = { widget?: MarkdownWidget; dashboardId: string };

export function MarkdownForm({ widget, dashboardId }: MarkdownFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const { mutate, isLoading: isUpdating } = useUpdateDashboard({
    onSuccess(data) {
      resetForm(data);
      close();
    },
  });

  const {
    formState: { errors, dirtyFields },
    handleSubmit,
    register,
    reset,
  } = useForm<MarkdownWidgetFieldValues>({
    defaultValues: { name: widget?.name, content: widget?.content || '' },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  function resetForm(updatedDashboard: Dashboard) {
    if (!widget?.id) {
      return;
    }

    const updatedWidget = updatedDashboard.configuration.widgets.find(
      ({ id }) => id === widget.id
    ) as MarkdownWidget;

    if (!updatedWidget) {
      return;
    }

    reset({ name: updatedWidget.name, content: updatedWidget.content });
  }

  function onSubmit({ name, content }: MarkdownWidgetFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();

    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 4, 4);

    const updatedDashboard = upsertWidget(
      dashboard,
      new MarkdownWidget(id, name, content, layout)
    );

    mutate({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="markdown" />
      <Field label="Widget name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>
      <Field label="Markdown content" error={errors.content}>
        <Textarea
          {...register('content')}
          rows={10}
          placeholder="Enter markdown content here..."
        />
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating} />
    </form>
  );
}
